import React, { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './OurServices.css';

import birthdayIcon from '../assets/icons/v1.svg';
import kittyPartyIcon from '../assets/icons/v2.svg';
import vrGameIcon from '../assets/icons/v3.svg';
import restaurantIcon from '../assets/icons/v4.svg';
import poolsideIcon from '../assets/icons/v5.svg';
import babyShowerIcon from '../assets/icons/v6.svg';
import namingIcon from '../assets/icons/v7.svg';
import coupleIcon from '../assets/icons/v8.svg';
import schoolTourIcon from '../assets/icons/v9.svg';

import e1 from '../assets/Ellipse/e1.svg';
import e2 from '../assets/Ellipse/e2.svg';
import e3 from '../assets/Ellipse/e3.svg';
import e4 from '../assets/Ellipse/e4.svg';
import e5 from '../assets/Ellipse/e5.svg';
import e6 from '../assets/Ellipse/e6.svg';
import e7 from '../assets/Ellipse/e7.svg';
import e8 from '../assets/Ellipse/e8.svg';
import e9 from '../assets/Ellipse/e9.svg';

const { Title, Text } = Typography;

const iconMap = {
  'v1': birthdayIcon,
  'v2': kittyPartyIcon,
  'v3': vrGameIcon,
  'v4': restaurantIcon,
  'v5': poolsideIcon,
  'v6': babyShowerIcon,
  'v7': namingIcon,
  'v8': coupleIcon,
  'v9': schoolTourIcon,
};

const ellipseMap = {
  'e1': e1,
  'e2': e2,
  'e3': e3,
  'e4': e4,
  'e5': e5,
  'e6': e6,
  'e7': e7,
  'e8': e8,
  'e9': e9,
};

const ServiceCard = ({ icon, title, back, index, onClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false });

  const cardVariants = {
    hidden: { opacity: 0, y: 20 + (index * 10) },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17,
        delay: 0.2 + (index * 0.1),
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      className="service-card"
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.92 }}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="service-icon-wrapper"
        style={{ backgroundImage: back }}
      >
        <img src={icon} alt={title} className="service-icon" />
      </div>
      <Text strong className="service-title">
        {title}
      </Text>
    </motion.div>
  );
};

const OurServices = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/service-categories`)
      .then((res) => {
        setCategories(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setCategories([]);
        setLoading(false);
      });
  }, []);

  const services = categories.map((category) => ({
    id: category._id,
    icon: iconMap[category.icon || 'v1'] || birthdayIcon,
    title: category.name,
    back: `url(${ellipseMap[category.ellipse || 'e1'] || e1})`,
  }));

  const ref = useRef(null);
  const isInView = useInView(ref, { once: false });

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
  };

  const renderWithDividers = (list) =>
    list.flatMap((service, index) => [
      <ServiceCard 
        key={index} 
        {...service} 
        index={index}
        onClick={() => handleCategoryClick(service.id)}
      />,
      index < list.length - 1 ? <div key={`divider-${index}`} className="divider" /> : null,
    ]);

  return (
    <div className="our-services-wrapper">
      <motion.h1
        ref={ref}
        className="title"
        initial={{ opacity: 0, y: -40 }}
        animate={isInView ? { opacity: 1, y: 0 } : {opacity: 0, y: -40}}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        Our Services
      </motion.h1>
      <motion.div
        className="our-services-grid"
        initial="hidden"
        animate={isInView ? "visible": "hidden"}
        transition={{ delayChildren: 0.4, staggerChildren: 0.2 }}
      >
        <AnimatePresence>
        {loading ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>Loading...</div>
        ) : services.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>No services available</div>
        ) : (
          renderWithDividers(services)
        )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default OurServices;
